import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Bell, 
  LogOut,
  ArrowLeft,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Activity,
  Crown,
  Globe,
  Eye,
  BarChart3,
  Map,
  MapPin,
  MousePointer,
  Calendar,
  ExternalLink,
  Menu,
  X,
  Trash2,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [secretKey, setSecretKey] = useState('');
  const [showLogin, setShowLogin] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [visitorAnalytics, setVisitorAnalytics] = useState(null);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAlerts, setShowAlerts] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [enrichingGeo, setEnrichingGeo] = useState(false);
  
  // Delete user states
  const [deleteModal, setDeleteModal] = useState({ show: false, user: null });
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [restoringUserId, setRestoringUserId] = useState(null);

  const adminToken = localStorage.getItem('admin_token');

  const fetchData = useCallback(async (token) => {
    try {
      const headers = { 'X-Admin-Token': token };
      
      const [analyticsRes, usersRes, alertsRes, usageRes, visitorRes, deletedRes] = await Promise.all([
        fetch(`${API}/api/admin/analytics`, { headers }),
        fetch(`${API}/api/admin/users?skip=${usersPage * 50}&limit=50`, { headers }),
        fetch(`${API}/api/admin/alerts?limit=10`, { headers }),
        fetch(`${API}/api/admin/feature-usage`, { headers }),
        fetch(`${API}/api/admin/visitor-analytics?days=30`, { headers }),
        fetch(`${API}/api/admin/deleted-users`, { headers })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
        setUsersTotal(data.total);
      }
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (usageRes.ok) setFeatureUsage(await usageRes.json());
      if (visitorRes.ok) setVisitorAnalytics(await visitorRes.json());
      if (deletedRes.ok) {
        const data = await deletedRes.json();
        setDeletedUsers(data.deleted_users || []);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  }, [usersPage]);

  useEffect(() => {
    const checkAuth = async () => {
      if (adminToken) {
        try {
          const res = await fetch(`${API}/api/admin/verify`, {
            headers: { 'X-Admin-Token': adminToken }
          });
          if (res.ok) {
            setIsAdmin(true);
            setShowLogin(false);
            await fetchData(adminToken);
          } else {
            localStorage.removeItem('admin_token');
          }
        } catch (error) {
          localStorage.removeItem('admin_token');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [adminToken, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret_key: secretKey })
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('admin_token', data.admin_token);
        setIsAdmin(true);
        setShowLogin(false);
        toast.success('Admin access granted');
        await fetchData(data.admin_token);
      } else {
        toast.error('Invalid admin key');
      }
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAdmin(false);
    setShowLogin(true);
    navigate('/');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData(adminToken);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleEnrichGeo = async () => {
    setEnrichingGeo(true);
    try {
      const res = await fetch(`${API}/api/admin/enrich-geo`, {
        method: 'POST',
        headers: { 'X-Admin-Token': adminToken }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Enriched ${data.enriched} visitor records`);
        await fetchData(adminToken);
      }
    } catch (error) {
      toast.error('Failed to enrich geo data');
    } finally {
      setEnrichingGeo(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user) return;
    
    setDeletingUserId(deleteModal.user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/users/${deleteModal.user.user_id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': adminToken }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`User deleted. Can be restored until ${new Date(data.can_restore_until).toLocaleDateString()}`);
        setDeleteModal({ show: false, user: null });
        await fetchData(adminToken);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleRestoreUser = async (userId) => {
    setRestoringUserId(userId);
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/restore`, {
        method: 'POST',
        headers: { 'X-Admin-Token': adminToken }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`User ${data.email} restored successfully`);
        await fetchData(adminToken);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to restore user');
      }
    } catch (error) {
      toast.error('Failed to restore user');
    } finally {
      setRestoringUserId(null);
    }
  };

  const handlePermanentDelete = async (userId) => {
    if (!window.confirm('This will PERMANENTLY delete this user and ALL their data. This cannot be undone. Are you sure?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/admin/deleted-users/${userId}/permanent`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': adminToken }
      });
      
      if (res.ok) {
        toast.success('User permanently deleted');
        await fetchData(adminToken);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to permanently delete user');
      }
    } catch (error) {
      toast.error('Failed to permanently delete user');
    }
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanColor = (plan) => {
    const colors = {
      free: 'text-gray-400 bg-gray-500/20',
      starter: 'text-blue-400 bg-blue-500/20',
      pro: 'text-emerald-400 bg-emerald-500/20',
      enterprise: 'text-purple-400 bg-purple-500/20'
    };
    return colors[plan] || colors.free;
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'deleted', label: 'Deleted Users', icon: Trash2, badge: deletedUsers.length || null },
    { id: 'visitors', label: 'Visitor Analytics', icon: Globe },
    { id: 'pages', label: 'Page Views', icon: Eye },
    { id: 'geography', label: 'Geography', icon: Map },
    { id: 'referrers', label: 'Traffic Sources', icon: ExternalLink },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-emerald-500 text-xl">Loading...</div>
      </div>
    );
  }

  if (showLogin || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-14 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase">
                Score Copilot
              </span>
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase">Admin Access</h1>
            <p className="text-gray-400 mt-2">Enter your admin secret key</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              placeholder="Admin Secret Key"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="admin-secret-input"
            />
            <Button 
              type="submit" 
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              data-testid="admin-login-btn"
            >
              Access Dashboard
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-400 hover:text-white text-sm flex items-center justify-center gap-2">
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-[#18181b] border-r border-white/10 transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <img src="/logo.jpg" alt="Score Copilot" className="h-8 w-auto" />
              <span className="font-heading text-sm font-bold text-white uppercase">Admin</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        
        <nav className="flex-1 p-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                activeSection === item.id
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={18} />
              {sidebarOpen && (
                <span className="text-sm flex items-center gap-2">
                  {item.label}
                  {item.badge && (
                    <span className="px-1.5 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-2 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut size={18} />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#0f0f10] sticky top-0 z-50">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-bold text-white uppercase">
                {menuItems.find(m => m.id === activeSection)?.label || 'Dashboard'}
              </h1>
              <p className="text-xs text-gray-500">Last 30 days</p>
            </div>

            <div className="flex items-center gap-3">
              {/* Alerts Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowAlerts(!showAlerts)}
                  className="relative p-2 text-gray-400 hover:text-white transition-colors"
                  data-testid="alerts-bell"
                >
                  <Bell size={24} />
                  {alerts?.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full text-xs text-white flex items-center justify-center">
                      {alerts.unread_count}
                    </span>
                  )}
                </button>

                {showAlerts && (
                  <div className="absolute right-0 mt-2 w-80 bg-[#1a1a1b] border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="p-3 border-b border-white/10">
                      <h3 className="font-semibold text-white">Recent Subscriptions</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {alerts?.alerts?.length > 0 ? (
                        alerts.alerts.map((alert, i) => (
                          <div key={i} className="p-3 border-b border-white/5 hover:bg-white/5">
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getPlanColor(alert.plan)}`}>
                                <Crown size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white font-medium truncate">{alert.user_name}</p>
                                <p className="text-xs text-gray-400 truncate">{alert.user_email}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${getPlanColor(alert.plan)}`}>
                                    {alert.plan?.toUpperCase()}
                                  </span>
                                  <span className="text-xs text-emerald-400">${alert.amount}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-400 text-sm">
                          No recent subscriptions
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleRefresh}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                disabled={refreshing}
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Users className="text-blue-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{analytics?.total_users || 0}</p>
                  <p className="text-sm text-gray-400">Total Users</p>
                  <p className="text-xs text-emerald-400 mt-1">+{analytics?.recent_signups || 0} this week</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-emerald-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">${analytics?.total_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-sm text-gray-400">Total Revenue</p>
                  <p className="text-xs text-emerald-400 mt-1">${analytics?.monthly_revenue?.toFixed(2) || '0.00'} this month</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Trophy className="text-purple-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{analytics?.total_organizations || 0}</p>
                  <p className="text-sm text-gray-400">Organizations</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                      <Activity className="text-yellow-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{analytics?.active_users_30d || 0}</p>
                  <p className="text-sm text-gray-400">Active Users (30d)</p>
                </div>
              </div>

              {/* Visitor Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <Eye className="text-cyan-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.total_visits || 0}</p>
                  <p className="text-sm text-gray-400">Page Views (30d)</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                      <MousePointer className="text-pink-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.unique_visitors || 0}</p>
                  <p className="text-sm text-gray-400">Unique Visitors</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <Globe className="text-orange-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.geographic?.length || 0}</p>
                  <p className="text-sm text-gray-400">Countries</p>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                      <ExternalLink className="text-indigo-400" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.referrers?.length || 0}</p>
                  <p className="text-sm text-gray-400">Traffic Sources</p>
                </div>
              </div>

              {/* Subscriptions by Plan */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-6">
                  <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Subscriptions by Plan</h2>
                  <div className="space-y-3">
                    {Object.entries(analytics?.subscriptions_by_plan || {}).map(([plan, count]) => (
                      <div key={plan} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded text-sm font-medium ${getPlanColor(plan)}`}>
                            {plan.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-white font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-6">
                  <h2 className="font-heading text-lg font-bold text-white uppercase mb-4">Feature Usage</h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Total Teams</span>
                      <span className="text-white font-bold">{featureUsage?.totals?.teams || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Total Players</span>
                      <span className="text-white font-bold">{featureUsage?.totals?.players || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Total Matches</span>
                      <span className="text-white font-bold">{featureUsage?.totals?.matches || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Completed Matches</span>
                      <span className="text-emerald-400 font-bold">{featureUsage?.totals?.completed_matches || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Section */}
          {activeSection === 'users' && (
            <div className="bg-[#1a1a1b] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="font-heading text-lg font-bold text-white uppercase">All Users</h2>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      setEnrichingGeo(true);
                      try {
                        const res = await fetch(`${API}/api/admin/enrich-user-geo`, {
                          method: 'POST',
                          headers: { 'X-Admin-Token': adminToken }
                        });
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(`Geo enriched: ${data.enriched} of ${data.total_ips} IPs`);
                          fetchData(adminToken);
                        }
                      } catch (e) {
                        toast.error('Failed to enrich geo data');
                      } finally {
                        setEnrichingGeo(false);
                      }
                    }}
                    disabled={enrichingGeo}
                    className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                    data-testid="enrich-geo-btn"
                  >
                    <Globe size={14} className={`mr-1 ${enrichingGeo ? 'animate-spin' : ''}`} />
                    {enrichingGeo ? 'Enriching...' : 'Enrich Geo'}
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white w-full sm:w-64"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">User</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Organization</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Plan</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Geolocation</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Signup Date</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Activity</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Revenue</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Last Login</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                                <span className="text-emerald-400 text-sm">{user.name?.charAt(0) || 'U'}</span>
                              </div>
                            )}
                            <div>
                              <p className="text-white text-sm font-medium">{user.name}</p>
                              <p className="text-gray-400 text-xs">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {user.organization ? (
                            <div>
                              <p className="text-white text-sm">{user.organization.name}</p>
                              <p className="text-gray-400 text-xs">{user.organization.subdomain}</p>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">No org</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPlanColor(user.subscription?.plan)}`}>
                            {user.subscription?.plan?.toUpperCase() || 'FREE'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.geolocation ? (
                            <div className="flex items-center gap-1.5">
                              <MapPin size={13} className="text-emerald-400 shrink-0" />
                              <div>
                                <p className="text-white text-xs font-medium">{user.geolocation.country || 'Unknown'}</p>
                                {user.geolocation.city && (
                                  <p className="text-gray-400 text-xs">{user.geolocation.city}</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-400 text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span>{user.activity?.teams || 0} teams</span>
                            <span>•</span>
                            <span>{user.activity?.players || 0} players</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-emerald-400 text-sm font-medium">
                            ${user.subscription?.amount?.toFixed(2) || '0.00'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-400 text-sm">
                            {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteModal({ show: true, user })}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-user-btn-${user.user_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-4 border-t border-white/10 flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  Showing {usersPage * 50 + 1}-{Math.min((usersPage + 1) * 50, usersTotal)} of {usersTotal}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUsersPage(Math.max(0, usersPage - 1))}
                    disabled={usersPage === 0}
                    className="text-gray-400"
                  >
                    <ChevronLeft size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUsersPage(usersPage + 1)}
                    disabled={(usersPage + 1) * 50 >= usersTotal}
                    className="text-gray-400"
                  >
                    <ChevronRight size={18} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Visitors Section */}
          {activeSection === 'visitors' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-white uppercase">Visitor Overview</h2>
                <Button
                  onClick={handleEnrichGeo}
                  disabled={enrichingGeo}
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Globe size={16} className={`mr-2 ${enrichingGeo ? 'animate-spin' : ''}`} />
                  {enrichingGeo ? 'Enriching...' : 'Enrich Geo Data'}
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.total_visits || 0}</p>
                  <p className="text-sm text-gray-400">Total Page Views</p>
                </div>
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.unique_visitors || 0}</p>
                  <p className="text-sm text-gray-400">Unique Visitors</p>
                </div>
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <p className="text-2xl font-bold text-white">
                    {visitorAnalytics?.total_visits && visitorAnalytics?.unique_visitors 
                      ? (visitorAnalytics.total_visits / visitorAnalytics.unique_visitors).toFixed(1)
                      : '0'}
                  </p>
                  <p className="text-sm text-gray-400">Pages per Visitor</p>
                </div>
                <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-4">
                  <p className="text-2xl font-bold text-white">{visitorAnalytics?.geographic?.length || 0}</p>
                  <p className="text-sm text-gray-400">Countries Reached</p>
                </div>
              </div>

              {/* Daily Visits Chart */}
              <div className="bg-[#1a1a1b] border border-white/10 rounded-xl p-6">
                <h3 className="font-heading text-md font-bold text-white uppercase mb-4">Daily Visits (Last 30 Days)</h3>
                <div className="h-48 flex items-end gap-1">
                  {Object.entries(visitorAnalytics?.daily_visits || {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .slice(-30)
                    .map(([date, count]) => {
                      const maxCount = Math.max(...Object.values(visitorAnalytics?.daily_visits || {1: 1}));
                      const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <div
                          key={date}
                          className="flex-1 bg-emerald-500/60 hover:bg-emerald-500 rounded-t transition-colors cursor-pointer group relative"
                          style={{ height: `${Math.max(height, 5)}%` }}
                          title={`${date}: ${count} visits`}
                        >
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black rounded text-xs text-white opacity-0 group-hover:opacity-100 whitespace-nowrap">
                            {date.slice(5)}: {count}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* Pages Section */}
          {activeSection === 'pages' && (
            <div className="bg-[#1a1a1b] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-heading text-lg font-bold text-white uppercase">Top Pages</h2>
              </div>
              <div className="divide-y divide-white/5">
                {visitorAnalytics?.page_views?.map(([page, views], i) => (
                  <div key={page} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">
                        {i + 1}
                      </span>
                      <span className="text-white">{page}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${(views / (visitorAnalytics?.page_views?.[0]?.[1] || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm w-16 text-right">{views} views</span>
                    </div>
                  </div>
                ))}
                {(!visitorAnalytics?.page_views || visitorAnalytics.page_views.length === 0) && (
                  <div className="p-8 text-center text-gray-400">
                    No page view data yet. Visits are being tracked.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Geography Section */}
          {activeSection === 'geography' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-white uppercase">Visitors by Country</h2>
                <Button
                  onClick={handleEnrichGeo}
                  disabled={enrichingGeo}
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Globe size={16} className={`mr-2 ${enrichingGeo ? 'animate-spin' : ''}`} />
                  {enrichingGeo ? 'Enriching...' : 'Enrich Geo Data'}
                </Button>
              </div>

              <div className="bg-[#1a1a1b] border border-white/10 rounded-xl overflow-hidden">
                <div className="divide-y divide-white/5">
                  {visitorAnalytics?.geographic?.map(([country, visits], i) => (
                    <div key={country} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400 text-sm font-bold">
                          {i + 1}
                        </span>
                        <span className="text-white">{country}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(visits / (visitorAnalytics?.geographic?.[0]?.[1] || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-gray-400 text-sm w-20 text-right">{visits} visits</span>
                      </div>
                    </div>
                  ))}
                  {(!visitorAnalytics?.geographic || visitorAnalytics.geographic.length === 0) && (
                    <div className="p-8 text-center text-gray-400">
                      <Globe size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No geographic data available yet.</p>
                      <p className="text-sm mt-2">Click "Enrich Geo Data" to fetch location info for visitors.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Referrers Section */}
          {activeSection === 'referrers' && (
            <div className="bg-[#1a1a1b] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-heading text-lg font-bold text-white uppercase">Traffic Sources</h2>
              </div>
              <div className="divide-y divide-white/5">
                {visitorAnalytics?.referrers?.map(([referrer, visits], i) => (
                  <div key={referrer} className="flex items-center justify-between px-4 py-3 hover:bg-white/5">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">
                        {i + 1}
                      </span>
                      <span className="text-white truncate max-w-md">{referrer}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full"
                          style={{ width: `${(visits / (visitorAnalytics?.referrers?.[0]?.[1] || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm w-16 text-right">{visits}</span>
                    </div>
                  </div>
                ))}
                {(!visitorAnalytics?.referrers || visitorAnalytics.referrers.length === 0) && (
                  <div className="p-8 text-center text-gray-400">
                    <ExternalLink size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No referrer data available yet.</p>
                    <p className="text-sm mt-2">Traffic sources will appear as visitors arrive from external links.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deleted Users Section */}
          {activeSection === 'deleted' && (
            <div className="bg-[#1a1a1b] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-heading text-lg font-bold text-white uppercase">Deleted Users</h2>
                <p className="text-sm text-gray-400 mt-1">Users can be restored within 7 days of deletion</p>
              </div>
              
              {deletedUsers.length > 0 ? (
                <div className="divide-y divide-white/5">
                  {deletedUsers.map((record) => {
                    const daysLeft = getDaysRemaining(record.expires_at);
                    return (
                      <div key={record.user_id} className="p-4 hover:bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                              <span className="text-red-400 text-sm">{record.user_data?.name?.charAt(0) || 'U'}</span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{record.user_data?.name}</p>
                              <p className="text-gray-400 text-sm">{record.user_data?.email}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-gray-400 text-xs">Deleted on</p>
                              <p className="text-white text-sm">{new Date(record.deleted_at).toLocaleDateString()}</p>
                            </div>
                            
                            <div className={`px-2 py-1 rounded text-xs font-medium ${
                              daysLeft <= 1 ? 'bg-red-500/20 text-red-400' : 
                              daysLeft <= 3 ? 'bg-yellow-500/20 text-yellow-400' : 
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleRestoreUser(record.user_id)}
                                disabled={restoringUserId === record.user_id}
                                className="bg-emerald-500 hover:bg-emerald-600"
                                data-testid={`restore-user-btn-${record.user_id}`}
                              >
                                <RotateCcw size={14} className={`mr-1 ${restoringUserId === record.user_id ? 'animate-spin' : ''}`} />
                                Restore
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePermanentDelete(record.user_id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                data-testid={`permanent-delete-btn-${record.user_id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {record.organization_data && (
                          <div className="mt-2 ml-13 pl-13">
                            <span className="text-xs text-gray-500">
                              Organization: {record.organization_data.name} ({record.organization_data.subdomain})
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-400">
                  <Trash2 size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No deleted users</p>
                  <p className="text-sm mt-2">Users you delete will appear here for 7 days before permanent removal.</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-400" size={24} />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Delete User</h3>
                <p className="text-gray-400 text-sm">This action can be undone within 7 days</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <p className="text-white font-medium">{deleteModal.user?.name}</p>
              <p className="text-gray-400 text-sm">{deleteModal.user?.email}</p>
              {deleteModal.user?.organization && (
                <p className="text-gray-500 text-xs mt-2">
                  Organization: {deleteModal.user.organization.name}
                </p>
              )}
            </div>
            
            <p className="text-gray-300 text-sm mb-6">
              Are you sure you want to delete this user? Their account and organization data will be stored for 7 days, during which you can restore them from the "Deleted Users" section.
            </p>
            
            <div className="flex items-center gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => setDeleteModal({ show: false, user: null })}
                className="text-gray-400"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={deletingUserId === deleteModal.user?.user_id}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-btn"
              >
                {deletingUserId === deleteModal.user?.user_id ? (
                  <>
                    <RefreshCw size={14} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-2" />
                    Delete User
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
